import React, { useEffect, useRef, useState } from 'react';

const WIDGET_ORIGIN = process.env.NODE_ENV === 'production'
  ? 'https://picklecourts.rent'
  : 'http://localhost:8081';
const VENUE_SLUG = 'the-medley-marketplace';

function safeCheckoutUrl(value: unknown, expectedParentOrigin: string): value is string {
  if (typeof value !== 'string') return false;
  try {
    const url = new URL(value);
    return url.origin === 'https://picklecourts.rent'
      && url.pathname.endsWith('/details')
      && url.searchParams.get('ref') === 'embed'
      && url.searchParams.get('parentOrigin') === expectedParentOrigin
      && /^[0-9a-f-]{36}$/i.test(url.searchParams.get('court') || '');
  } catch {
    return false;
  }
}

export default function WidgetDemoScreen() {
  const frame = useRef<HTMLIFrameElement>(null);
  const bookingCard = useRef<HTMLElement>(null);
  const closeButton = useRef<HTMLButtonElement>(null);
  const [status, setStatus] = useState('Loading live Medley availability…');
  const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);
  const parentOrigin = window.location.origin;
  const widgetUrl = `${WIDGET_ORIGIN}/embed/${VENUE_SLUG}?parentOrigin=${encodeURIComponent(parentOrigin)}`;

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (
        event.origin !== WIDGET_ORIGIN
        || event.source !== frame.current?.contentWindow
        || !data
        || data.type !== 'picklecourts.embed.checkout'
        || data.version !== 1
        || data.source !== 'embedded_widget'
        || data.venueSlug !== VENUE_SLUG
        || !safeCheckoutUrl(data.checkoutUrl, parentOrigin)
      ) return;
      setStatus('Secure checkout opened on this page.');
      setCheckoutUrl(data.checkoutUrl);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!checkoutUrl) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeButton.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setCheckoutUrl(null);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
      bookingCard.current?.focus();
    };
  }, [checkoutUrl]);

  return (
    <div className="widget-demo">
      <header className="widget-demo__hero">
        <nav className="widget-demo__nav" aria-label="Medley demo navigation">
          <a className="widget-demo__brand" href="/widget-demo" aria-label="Medley home">
            <span className="widget-demo__brand-mark" aria-hidden="true">M</span>
            <span><strong>MEDLEY</strong><small>Marketplace</small></span>
          </a>
          <span className="widget-demo__nav-note"><i aria-hidden="true" /> Live booking demo</span>
        </nav>
        <div className="widget-demo__hero-content">
          <span className="widget-demo__eyebrow">Courts · wellness · community</span>
          <h1>Your next match<br />starts at Medley.</h1>
          <p>Browse real-time court availability and reserve your time without ever leaving the Medley experience.</p>
          <div className="widget-demo__proof" aria-label="Booking highlights">
            <span><b aria-hidden="true">✓</b> Live availability</span>
            <span><b aria-hidden="true">✓</b> Secure checkout</span>
            <span><b aria-hidden="true">✓</b> Instant confirmation</span>
          </div>
        </div>
      </header>

      <main className="widget-demo__main">
        <section className="widget-demo__story" aria-labelledby="widget-demo-story-title">
          <span className="widget-demo__section-label">Your local third place</span>
          <h2 id="widget-demo-story-title">Play belongs here.</h2>
          <p>Medley brings people together around movement, recreation, and a good match. Pick a live court time and finish your reservation in a secure checkout that opens right here.</p>
          <div className="widget-demo__feature-list">
            <div><span aria-hidden="true">01</span><p><strong>Choose your time</strong><small>See current court openings at a glance.</small></p></div>
            <div><span aria-hidden="true">02</span><p><strong>Checkout in place</strong><small>A focused modal keeps you inside Medley.</small></p></div>
            <div><span aria-hidden="true">03</span><p><strong>Get confirmed</strong><small>Your booking is recorded instantly.</small></p></div>
          </div>
          <aside className="widget-demo__security-note">
            <span aria-hidden="true">◆</span>
            <p><strong>Powered securely by PickleCourts</strong><small>The picker has public availability access only. No administrator credentials or payment secrets are exposed.</small></p>
          </aside>
        </section>

        <section
          ref={bookingCard}
          className="widget-demo__booking"
          aria-label="Book a Medley court"
          tabIndex={-1}
        >
          <div className="widget-demo__booking-header">
            <div><span className="widget-demo__section-label">The Medley Marketplace</span><strong>Reserve a court</strong></div>
            <span className="widget-demo__live"><i aria-hidden="true" /> Live</span>
          </div>
          <iframe
            ref={frame}
            src={widgetUrl}
            title="Medley court availability"
            sandbox="allow-scripts allow-same-origin"
            referrerPolicy="strict-origin-when-cross-origin"
            onLoad={() => setStatus('Live availability loaded securely.')}
          />
          <p className="widget-demo__status" aria-live="polite"><span aria-hidden="true">●</span>{status}</p>
        </section>
      </main>

      <footer className="widget-demo__footer"><span>MEDLEY</span><p>Made for good games and even better company.</p></footer>

      {checkoutUrl && (
        <div className="widget-demo__checkout-backdrop" role="presentation" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setCheckoutUrl(null);
        }}>
          <section className="widget-demo__checkout-modal" role="dialog" aria-modal="true" aria-labelledby="medley-checkout-title">
            <header>
              <div>
                <span className="widget-demo__section-label">Secure PickleCourts checkout</span>
                <h2 id="medley-checkout-title">Finish your reservation</h2>
              </div>
              <button ref={closeButton} type="button" onClick={() => setCheckoutUrl(null)} aria-label="Close checkout">×</button>
            </header>
            <div className="widget-demo__checkout-frame-wrap">
              <iframe
                src={checkoutUrl}
                title="Secure PickleCourts checkout"
                sandbox="allow-forms allow-modals allow-popups allow-same-origin allow-scripts"
                allow="payment *"
                referrerPolicy="strict-origin-when-cross-origin"
              />
            </div>
            <footer><span><i aria-hidden="true" /> Encrypted checkout</span><button type="button" onClick={() => setCheckoutUrl(null)}>Back to availability</button></footer>
          </section>
        </div>
      )}
    </div>
  );
}
